import styled from 'styled-components';

export const STable = styled.table`
  width: 100%;
`;

export const Sth = styled.th`
  border: 0;
  background: none;
  font-weight: 400;
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 12px;

  @media (max-width: 768px) {
    padding: 12px 5px;
  }
`;

export const Std = styled.td`
  vertical-align: middle;
  padding: 12px;
  border-top: ${props => props.theme.colors.border};
`;

export const HiddenInput = styled.input`
  display:none;
`;

export const StdSecondary = styled.td`
  vertical-align: middle;
  padding: 12px;
  border-top: ${props => props.theme.colors.border};

  @media (max-width: 768px) {
    padding: 12px 5px;
  }
`;

export const WrapDiv = styled.div<{color?: string}>`
  display: inline;
  svg {
    ${props => props.color && `fill: ${props.theme.deviceStatus[props.color]};`}
    width: 32px;
    height: 32px;
  }
`
export const HorizontalRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
`;

export const SelectWrap = styled.div`
  position: absolute;
  width: 200px;
`;

export const LabelAndDropdownWrap = styled.div`
  height: 75px;
`;

/* ── Sensor configuration surface ─────────────────────────────────────── */

export const ConfigPage = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

/* Card surface for the whole configuration */
export const ConfigPanel = styled.div`
  width: 100%;
  border: ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.colors.borderRadius};
  background: ${props => props.theme.colors.backgroundSecondary};
  overflow: hidden;
`;

/* Identity + master-active header */
export const ConfigHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.margins.more};
  flex-wrap: wrap;
  padding: 24px 32px;
  background: ${props => props.theme.colors.backgroundPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};
`;

export const HeaderIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 240px;
`;

export const Avatar = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme.colors.borderColor};
  background: ${props => props.theme.colors.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 30px;
    height: 30px;
  }

  path {
    fill: ${props => props.theme.colors.textPrimary};
  }
`;

export const IdentityMeta = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const IdentityName = styled.h2`
  margin: 0;
  font-weight: 500;
  font-size: ${props => props.theme.font.large};
  color: ${props => props.theme.colors.textPrimary};
  line-height: 1.2;
`;

export const IdentitySub = styled.span`
  margin-top: 2px;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textSecondary};
`;

/* Right-hand master active control */
export const ActiveControl = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  max-width: 320px;
`;

export const ActiveLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ActiveLabel = styled.span`
  font-size: ${props => props.theme.font.normal};
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
`;

export const ActiveHint = styled.p`
  margin: 0;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

/* Body holds the numbered sections */
export const ConfigBody = styled.div`
  padding: 8px 32px;
  transition: opacity 0.2s ease;
`;

/* One numbered configuration step */
export const Section = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 24px 40px;
  padding: 28px 0;
  border-bottom: 1px solid ${props => props.theme.colors.borderColor};

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: flex-start;
`;

export const SectionTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: ${props => props.theme.font.normal};
  font-weight: 600;
  color: ${props => props.theme.colors.textPrimary};
`;

export const SectionDesc = styled.p`
  margin: 4px 0 0;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.5;
`;

export const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

/* Selectable icon cards for the primary choice (placement / function) */
export const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;

export const OptionCard = styled.button<{ selected?: boolean }>`
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: ${props => props.theme.colors.borderRadius};
  border: 2px solid ${props =>
    props.selected ? props.theme.colors.accent : props.theme.colors.borderColor};
  background: ${props => props.theme.colors.backgroundPrimary};
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

export const OptionIcon = styled.div<{ selected?: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  svg {
    width: 26px !important;
    height: 26px !important;
  }

  path {
    fill: ${props =>
      props.selected ? props.theme.colors.accent : props.theme.colors.textPrimary};
  }
`;

export const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const OptionTitle = styled.span`
  font-size: ${props => props.theme.font.normal};
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
`;

export const OptionDesc = styled.span`
  margin-top: 2px;
  font-size: ${props => props.theme.font.small};
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.4;
`;

/* Inline toggle row (e.g. notification) */
export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ToggleText = styled.span`
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
`;

/* Constrains dropdowns to the system control width */
export const ConfigControl = styled.div`
  width: 100%;
  max-width: 300px;
`;

export const TimeRangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const TimeLabel = styled.span`
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
`;

export const ConfigTimeInput = styled.input`
  border-radius: 5px;
  line-height: 2.3;
  height: 40px;
  width: 130px;
  padding: 0 15px;
  font-size: ${props => props.theme.font.normal};
  color: ${props => props.theme.colors.textPrimary};
  background: ${props =>
    props.disabled
      ? 'rgba(202,202,203,0.15)'
      : props.theme.colors.backgroundPrimary};
  ${props => (props.disabled ? 'cursor: not-allowed;' : '')}
  border: ${props => props.theme.colors.border};
`;

/* Footer action bar */
export const ConfigActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 32px;
  background: ${props => props.theme.colors.backgroundPrimary};
  border-top: 1px solid ${props => props.theme.colors.borderColor};
`;

export const AddWrap = styled.div`
  justify-content: flex-end;
  display: flex;
  padding: 0 0 10px 0;
`;
